import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { chunk } from "../lib/batchedIn";
import type { QueueRow } from "../lib/reviewQueue";
import { reviewSheet, type ReviewSheet, type SheetPlan, type SheetSession } from "../lib/reviewSheet";
import { PLAN_TREE_SELECT, planSections, type PlanTreeSectionRow } from "../lib/setVerdict";

/* THE REVIEW'S READS (W82 step 3) — for ONE queue row: the client's records of that day, the day's assigned copy, and the
 * movements' names and rules. Read through RLS as the signed-in coach, under the policies the queue already meets
 * (`workout_sessions` → `workout_session_exercises` → `set_logs` / `form_clips` for a client of this coach — `form_clips`
 * through the ACTIVE-coach read of `20260905120000`; the coach's own `coach_assignments` and copies; the library).
 *
 * ⚠️ THE DAY'S RECORDS ARE READ SO THEY CAN BE COUNTED, NOT SUMMED — `reviewSheet` reads `row.sessionId` alone (the queue's
 * choice), and says how many other records it left out.
 * ⚠️ THE RULE IS READ OFF THE LIBRARY ROW (`coach_exercises.progression_config`), where it lives; the copy carries none.
 * A failed read is a failed page, never an empty workout. */

export type SheetState =
  | { status: "loading" }
  | { status: "error"; message: string }
  /** `kmKeys` — the coach exercises whose field is `distance_km` (W136 B2 S3r3 A3: the Next-week column says km too). */
  | { status: "ready"; sheet: ReviewSheet | null; kmKeys: ReadonlySet<string>; media: ReadonlyMap<string, ExerciseMedia> };

/** An exercise's own demo (the library row's), drawn as the square under its title in the review card (his word 2026-10-07
 *  ~12:20 and ~13:40: "each could have its square video container under the title"). Keyed by the coach exercise id. */
export type ExerciseMedia = { readonly demoVideoUrl: string | null; readonly thumbnailUrl: string | null };

type LogRow = { set_number: number | null; status: string; reps_completed: number | null; weight_kg: number | string | null; hold_seconds: number | null; rpe: number | string | null; note: string | null;
  distance_m?: number | string | null; rounds?: number | null };
type ClipRow = { id: string; set_number: number | null; media_kind: string; storage_path: string; recorded_at: string | null };
type WseRow = { id: string; position: number | null; section_role: string | null; coach_exercise_id: string | null; exercise_id: string | null; set_logs: LogRow[] | null; form_clips: ClipRow[] | null };
type SessionRow = { id: string; user_id: string; kind: string; scheduled_date: string | null; duration_actual_minutes: number | null; performance_notes: string | null; workout_session_exercises: WseRow[] | null };
type TreeRow = { name: string | null; kind: string | null; coach_workout_sections: PlanTreeSectionRow[] | null };
type AssignmentRow = { coach_workouts: TreeRow | TreeRow[] | null };

const num = (v: number | string | null): number | null => {
  if (v === null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function useReviewSheet(row: QueueRow | null): SheetState {
  const [state, setState] = useState<SheetState>({ status: "loading" });
  useEffect(() => {
    if (row === null) return;
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      const fail = (message: string) => { if (!cancelled) setState({ status: "error", message }); };
      // The chosen record first, for its day; then every record of that day for the count.
      const one = await supabase.from("workout_sessions").select("scheduled_date").eq("id", row.sessionId).maybeSingle();
      if (cancelled) return;
      if (one.error !== null) return fail(one.error.message);
      const day = (one.data as { scheduled_date: string | null } | null)?.scheduled_date?.slice(0, 10) ?? null;

      const select = "id, user_id, kind, scheduled_date, duration_actual_minutes, performance_notes, workout_session_exercises(id, position, section_role, coach_exercise_id, exercise_id, set_logs(set_number, status, reps_completed, weight_kg, hold_seconds, rpe, note, distance_m, rounds), form_clips(id, set_number, media_kind, storage_path, recorded_at))";
      const r = day === null
        ? await supabase.from("workout_sessions").select(select).eq("id", row.sessionId)
        : await supabase.from("workout_sessions").select(select).eq("user_id", row.clientId).eq("scheduled_date", day);
      if (cancelled) return;
      if (r.error !== null) return fail(r.error.message);
      const sessionRows = (r.data ?? []) as unknown as SessionRow[];

      let plan: SheetPlan | null = null;
      if (day !== null) {
        const a = await supabase.from("coach_assignments")
          .select(`coach_workouts!workout_id(name, kind, ${PLAN_TREE_SELECT})`)
          .eq("client_id", row.clientId).eq("scheduled_date", day);
        if (cancelled) return;
        if (a.error !== null) return fail(a.error.message);
        // ⚠️ An embed comes back as OBJECT or ARRAY by inferred relationship (the codex) — both are read.
        const trees = ((a.data ?? []) as unknown as AssignmentRow[])
          .map((x) => (Array.isArray(x.coach_workouts) ? (x.coach_workouts[0] ?? null) : x.coach_workouts))
          .filter((w): w is TreeRow => w !== null && w.kind !== "task");
        // ONE training assignment or no plan — the queue's own rule for "written" (0 or 2+ → null).
        const tree = trees.length === 1 ? trees[0] : undefined;
        if (tree !== undefined) {
          plan = { workoutName: tree.name, sections: planSections(tree.coach_workout_sections) };
        }
      }

      const wses = sessionRows.flatMap((s) => s.workout_session_exercises ?? []);
      const coachIds = new Set<string>(wses.map((w) => w.coach_exercise_id).filter((x): x is string => x !== null));
      for (const s of plan?.sections ?? []) for (const e of s.exercises) if (e.exercise_id !== null) coachIds.add(e.exercise_id);
      const engineIds = [...new Set(wses.map((w) => w.exercise_id).filter((x): x is string => x !== null))];
      const names = new Map<string, string>();
      const rules = new Map<string, unknown>();
      // W136 B2 S3 — the coach's field picks a run's unit (`distance_km` → km), as the phone's review reads it.
      const kmKeys = new Set<string>();
      const media = new Map<string, ExerciseMedia>();
      for (const part of chunk([...coachIds])) {
        const q = await supabase.from("coach_exercises").select("id, name, progression_config, tracking_fields, demo_video_url, thumbnail_url").in("id", part);
        if (cancelled) return;
        if (q.error !== null) return fail(q.error.message);
        for (const e of (q.data ?? []) as { id: string; name: string; progression_config: unknown; tracking_fields: string[] | null;
                                            demo_video_url: string | null; thumbnail_url: string | null }[]) {
          names.set(e.id, e.name); rules.set(e.id, e.progression_config);
          media.set(e.id, { demoVideoUrl: e.demo_video_url, thumbnailUrl: e.thumbnail_url });
          if ((e.tracking_fields ?? []).includes("distance_km")) kmKeys.add(e.id);
        }
      }
      for (const part of chunk(engineIds)) {
        const q = await supabase.from("exercises").select("id, name").in("id", part);
        if (cancelled) return;
        for (const e of q.data ?? []) names.set(e.id, e.name);
      }

      const sessions: SheetSession[] = sessionRows.map((s) => ({
        id: s.id, userId: s.user_id, kind: s.kind, scheduledDate: s.scheduled_date, minutes: s.duration_actual_minutes, note: s.performance_notes,
        exercises: (s.workout_session_exercises ?? []).map((w) => ({
          id: w.id, position: w.position, section_role: w.section_role, key: w.coach_exercise_id ?? w.exercise_id, coachExerciseId: w.coach_exercise_id,
          logs: (w.set_logs ?? []).map((l) => ({ ...l, weight_kg: num(l.weight_kg), rpe: num(l.rpe), distance_m: num(l.distance_m ?? null) })),
          clips: w.form_clips ?? [],
        })),
      }));
      if (!cancelled) setState({ status: "ready", sheet: reviewSheet({ row, sessions, plan, names, rules, kmKeys }), kmKeys, media });
    })();
    return () => { cancelled = true; };
  }, [row]);
  return row === null ? { status: "loading" } : state;
}
